import { useMemo, useRef } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import {
  defaultWorkboardPerformanceQueryDateFrom,
  DetailsLoading,
  flowSteps,
  IFlow,
  IFlowNavigationTab,
  PERFORMANCE_DEFAULT_LIMIT,
  performanceGraphqlFlattenWorks,
  PerformanceWorkboardKey,
  performanceWorkQuery,
  usePerformanceGraphQLParameters,
} from '@diana/core';
import { NavigationRenderer } from '../../../../components/NavigationTabs/NavigationRenderer';
import { WorkPageTabsEnum } from '../../WorkPage.constants';

export function Navigation({ flow }: { flow?: IFlow | null }) {
  const worksLengthRef = useRef<number>(0);

  const flowId = flow?.flowOriginId;
  const flowStepsQueries = flowSteps.getById({ id: flowId! });
  const { isLoading: isFlowStepsLoading, data: steps } = useQuery({
    queryKey: flowStepsQueries.key,
    queryFn: flowStepsQueries.fn,
    staleTime: Infinity,
    enabled: Boolean(flowId),
  });

  const isFlowsLoaded = Boolean(flow) && Boolean(steps?.length);
  const queryParameters = usePerformanceGraphQLParameters({
    flows: flow ? [flow] : [],
    filters: [],
    filtersValues: {},
    dateFrom: defaultWorkboardPerformanceQueryDateFrom,
    activeStepIds: steps?.map((e) => e.id),
  });

  const performanceWorkQueryKeys = useMemo(
    () => [PerformanceWorkboardKey, queryParameters],
    [queryParameters],
  );

  const { data, fetchNextPage, isFetching, isFetchingNextPage } = useInfiniteQuery({
    queryKey: performanceWorkQueryKeys,
    queryFn: async ({ pageParam }) => {
      const data = await performanceWorkQuery
        .get(queryParameters)
        .fn({ skip: pageParam as number });

      if (data.length === PERFORMANCE_DEFAULT_LIMIT) {
        setTimeout(() => fetchNextPage(), 0);
      }

      return data;
    },
    enabled: isFlowsLoaded,
    staleTime: 60000 * 2, // 2 minutes
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    initialPageParam: 0,
    getNextPageParam: (_, allPages) => performanceGraphqlFlattenWorks(allPages).length,
  });

  const isWorkLoading = isFetching || isFetchingNextPage || isFlowStepsLoading;
  const unfilteredWorks = performanceGraphqlFlattenWorks(data);
  const works = unfilteredWorks.filter((work) => work.flowOriginId === flow?.flowOriginId);
  worksLengthRef.current = unfilteredWorks.length;

  if (isWorkLoading) {
    return <DetailsLoading />;
  }

  const config = (flow?.flowFeatures?.leftPanel?.tabs?.find(
    (t) => t.tab === WorkPageTabsEnum.Navigation,
  ) ?? {}) as IFlowNavigationTab;

  return <NavigationRenderer works={works} {...config} />;
}
